import { useEffect } from 'react';

interface SeoProps {
  title?: string;
  description?: string;
  canonical?: string;
  jsonLd?: object | object[];
}

const SITE_URL = 'https://lasoxp.com';

function setMeta(selector: string, attr: 'content' | 'href', value: string) {
  if (typeof document === 'undefined') return;
  const el = document.head.querySelector<HTMLMetaElement | HTMLLinkElement>(selector);
  if (el) el.setAttribute(attr, value);
}

/**
 * Per-route SEO controller. Uses imperative head updates so it works alongside
 * the static <head> tags from index.html without needing react-helmet-async
 * (which has not yet released a React 19 compatible peer range).
 */
export function Seo({ title, description, canonical, jsonLd }: SeoProps) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    if (description) {
      setMeta('meta[name="description"]', 'content', description);
      setMeta('meta[property="og:description"]', 'content', description);
      setMeta('meta[property="twitter:description"]', 'content', description);
    }
    if (title) {
      setMeta('meta[property="og:title"]', 'content', title);
      setMeta('meta[property="twitter:title"]', 'content', title);
    }
    const canonicalHref = canonical
      ? canonical.startsWith('http')
        ? canonical
        : `${SITE_URL}${canonical}`
      : null;
    if (canonicalHref) {
      let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.rel = 'canonical';
        document.head.appendChild(link);
      }
      link.href = canonicalHref;
      setMeta('meta[property="og:url"]', 'content', canonicalHref);
    }

    const scripts: HTMLScriptElement[] = [];
    if (jsonLd) {
      const payloads = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      for (const payload of payloads) {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.text = JSON.stringify(payload);
        document.head.appendChild(script);
        scripts.push(script);
      }
    }

    return () => {
      document.title = prevTitle;
      for (const script of scripts) script.remove();
    };
  }, [title, description, canonical, jsonLd]);

  return null;
}
